import React, { forwardRef, SyntheticEvent, useState } from 'react'
import { RootState } from 'src/store/store'
import { CountryCodeType } from 'src/types/phoneinput'
import { CountryPhoneInputType } from 'src/components/etc/CountryPhoneInput/index'
import { useAppSelector } from 'src/hooks/redux/useAppSelector'
import styled, { css } from 'styled-components'
import en from 'react-phone-number-input/locale/en.json'
import { COLOR } from 'src/constants/colors'
import Input from 'src/components/atomic/atom/Input'
import { useTranslation } from 'react-i18next'
import { getCountryCallingCode } from 'react-phone-number-input'
import useMediaQueryHook from 'src/hooks/useMediaQuery.hook'
import useOutsideClickDetectorHook from 'src/hooks/useOutsideClickDetector.hook'
import { formatCountryName } from 'src/utils/common.utils'

const CountryPhoneInputTemplate = forwardRef(
  (props: { data: CountryPhoneInputType }, inputRef?: React.Ref<HTMLInputElement>) => {
    const { onBlurPhoneInput, CountrySelectHandler, state, CountryList, customCountryLabel } =
      props.data
    const { tempCountryInfo } = state
    const { t } = useTranslation()
    const { setMediaStyle } = useMediaQueryHook()
    const [selectShow, setSelectShow] = useState(false)
    const { TempValue, GPNumber } = useAppSelector((state: RootState) => state.HomePage)
    const { Connection } = useAppSelector((state: RootState) => state.connection)
    const { outside_ref } = useOutsideClickDetectorHook(selectShow, () => {
      CountrySelectHandler(tempCountryInfo, TempValue.CountryCode.country as CountryCodeType)
      setSelectShow(false)
    })

    return (
      <CountryPhoneInputWrap ref={outside_ref}>
        <CustomSelect
          onClick={() => setSelectShow((prev: boolean) => !prev)}
          $show={`${selectShow}`}
          disabled={!Connection}
          tabIndex={0}
          id={'country'}
        >
          <label>{tempCountryInfo}</label>
          <CustomSelectArrow $show={`${selectShow}`} />
        </CustomSelect>
        <CustomSelectOptions
          $show={`${selectShow}`}
          $max_height={setMediaStyle('517px', '517px', '277px')}
          onClick={() => setSelectShow((prev: boolean) => !prev)}
        >
          {CountryList?.map((country) => (
            <React.Fragment key={country}>
              <CustomOption
                value={`${en[country]} (+${getCountryCallingCode(country)})`}
                onClick={(e: SyntheticEvent) =>
                  CountrySelectHandler(
                    (e.target as HTMLInputElement).getAttribute('value') as string,
                    country,
                  )
                }
                className={`country_${formatCountryName(tempCountryInfo)}`}
              >
                {customCountryLabel(country)}
              </CustomOption>
            </React.Fragment>
          ))}
        </CustomSelectOptions>
        <Input
          ref={inputRef}
          onBlur={onBlurPhoneInput}
          disabled={!Connection}
          option={'emergency_contact'}
          placeholder={
            t('HOME.CONTACT_PLACEHOLDER', { defaultValue: 'Enter emergency contact' }) as string
          }
          value={TempValue.GPNumber}
          style={{
            background: COLOR.black2,
            padding: GPNumber === '' && TempValue.GPNumber === '' ? '0 16px' : '0 16px 0 55px',
          }}
        />
        <CustomInputLabel>
          {GPNumber === '' && TempValue.GPNumber === ''
            ? '\u000A'
            : `+${getCountryCallingCode(TempValue?.CountryCode?.country as CountryCodeType)}`}
        </CustomInputLabel>
      </CountryPhoneInputWrap>
    )
  },
)
export default CountryPhoneInputTemplate

const CountryPhoneInputWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  position: relative;
`
const CustomSelect = styled.div<{ $show: string; disabled: boolean }>`
  position: relative;
  z-index: 2;
  width: 100%;
  border-radius: 12px;
  border: 1px solid ${(props) => (props.$show === 'true' ? COLOR.brand : COLOR.black20)};
  box-shadow: ${(props) => (props.$show === 'true' ? '0 3px 4px 0 rgba(0, 0, 0, 0.1); ' : 'unset')};
  background: #fff;
  list-style: none;
  box-sizing: border-box;
  padding: 13px 14px;
  cursor: pointer;
  margin: 0;
  font-family: 'NotoSansRegular', serif;
  font-size: 16px;
  line-height: 23px;
  display: block;
  text-align: center;
  > label {
    user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    -webkit-user-select: none;
    cursor: pointer;
  }
  ${(props) =>
    props.disabled === true &&
    css`
      background: ${COLOR.black2};
      pointer-events: none;
    `};
`
const CustomSelectArrow = styled.div<{ $show: string }>`
  display: block;
  position: absolute;
  right: 16px;
  top: 50%;
  width: 16px;
  height: 16px;
  transform: translate(0, -50%);

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 2px;
    right: 6px;
    width: 2px;
    height: 18px;
    border-bottom: 10px solid ${COLOR.black20};
    box-sizing: border-box;
    transform-origin: center;
    transition: all 0.2s;
  }

  &:before {
    transform: ${(props) => (props.$show === 'true' ? 'rotate(45deg)' : 'rotate(135deg)')};
    top: ${(props) => (props.$show === 'true' ? '-4px' : '2px')};
  }

  &:after {
    transform: ${(props) => (props.$show === 'true' ? 'rotate(-45deg)' : 'rotate(-135deg)')};
    top: ${(props) => (props.$show === 'true' ? '-4px' : '2px')};
  }
`
const CustomSelectOptions = styled.ul<{ $show: string; $max_height: string }>`
  transition: all 0.2s;
  position: absolute;
  width: 100%;
  cursor: pointer;
  z-index: 1;
  overflow-y: auto;
  border-radius: 12px;
  outline: ${(props) => (props.$show === 'true' ? '1px solid #ccc' : 'unset')};
  background: #fff;
  box-sizing: border-box;
  box-shadow: 0 3px 4px 0 rgba(0, 0, 0, 0.1);
  max-height: ${(props) => (props.$show === 'true' ? props.$max_height : '0')};
  padding-top: ${(props) => (props.$show === 'true' ? '15px' : '0')};
  top: 36px;
  right: 0;
  left: 0;
  margin: auto;
`
const CustomOption = styled.li`
  position: relative;
  z-index: 2;
  display: block;
  text-align: center;
  width: 100%;
  border: 0;
  outline: 0;
  background: none;
  box-sizing: border-box;
  padding: 13.5px;
  font-family: 'NotoSansRegular', serif;
  font-size: 16px;
  line-height: 23px;
  transition: background-color 0.2s ease-in;

  &:hover {
    background-color: #fafafa;
  }
`
const CustomInputLabel = styled.span`
  font-size: 16px;
  font-family: 'NotoSansRegular', serif;
  height: 0;
  position: relative;
  top: -45.5px;
  left: 16px;
`
